import {Popover, PopoverButton, PopoverPanel} from "@headlessui/react";
import {type CSSProperties, ElementType, ReactNode, useEffect, useRef} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsTooltip} from "./OtsTooltip";

export type OtsPopoverPlacement = "bottom start" | "bottom end" | "top start" | "top end";
export type OtsPopoverDensity = "compact" | "default";

export type OtsPopoverRenderApi = {
  /** Closes the panel, e.g. before opening a dialog from inside it. */
  close: () => void;
};

export type OtsPopoverProps = {
  trigger: ReactNode;
  children: ReactNode | ((api: OtsPopoverRenderApi) => ReactNode);
  /** Called when the panel opens or closes, not on first render. */
  onOpenChange?: (open: boolean) => void;
  /** Renders the trigger as another component, e.g. catalyst's NavbarItem for a navbar entry. */
  buttonAs?: ElementType;
  density?: OtsPopoverDensity;
  placement?: OtsPopoverPlacement;
  tooltip?: ReactNode;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  ariaLabel?: string;
  /** Active toggle state, independent of whether the popover is open. */
  pressed?: boolean;
  className?: string;
  buttonClassName?: string;
  panelClassName?: string;
  /** Caps the floating container while allowing the anchor to reduce it further to fit the viewport. */
  maxHeight?: string;
  /** Minimum distance from the viewport edges, in pixels. */
  viewportPadding?: number;
  /**
   * Lets content reach past the panel instead of the panel scrolling: for forms whose dropdowns open over what is
   * below, which would otherwise grow a scrollbar in the panel.
   */
  allowOverflow?: boolean;
};

const OpenChangeObserver = ({open, onOpenChange}: {open: boolean; onOpenChange?: (open: boolean) => void}) => {
  const previous = useRef(open);
  useEffect(() => {
    if (previous.current === open) return;
    previous.current = open;
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  return null;
};

/** Theme- and density-aware click popover suitable for both app pages and compact GWT mounts. */
export const OtsPopover = ({
  trigger,
  children,
  onOpenChange,
  buttonAs,
  density = "default",
  placement = "bottom start",
  tooltip,
  brand,
  colorScheme,
  ariaLabel,
  pressed,
  className,
  buttonClassName,
  panelClassName,
  maxHeight,
  viewportPadding,
  allowOverflow = false,
}: OtsPopoverProps) => {
  const button = buttonAs
    ? <PopoverButton as={buttonAs} aria-label={ariaLabel} aria-pressed={pressed} className={buttonClassName}>{trigger}</PopoverButton>
    : <PopoverButton type="button" aria-label={ariaLabel} aria-pressed={pressed} className={buttonClassName}>{trigger}</PopoverButton>;

  return <Popover className={["relative inline-flex", className].filter(Boolean).join(" ")}>
    {({open, close}) => <>
      <OpenChangeObserver open={open} onOpenChange={onOpenChange}/>
      {tooltip != null ? <OtsTooltip content={tooltip} density={density} brand={brand} colorScheme={colorScheme} disabled={open}>{button}</OtsTooltip> : button}
      <PopoverPanel
        anchor={placement}
        style={maxHeight || viewportPadding != null ? {
          ...(maxHeight ? {"--anchor-max-height": maxHeight} : {}),
          ...(viewportPadding != null ? {"--anchor-padding": `${viewportPadding}px`} : {}),
        } as CSSProperties : undefined}
        portal
        className={[
          "z-[1200] w-max max-w-[calc(100vw-1rem)] focus:outline-none",
          allowOverflow ? "!overflow-visible !max-h-none" : "",
          density === "compact" ? "[--anchor-gap:0.25rem]" : "[--anchor-gap:0.5rem]",
        ].join(" ")}>
        <OtsThemeRoot
          brand={brand}
          colorScheme={colorScheme}
          data-density={density}
          className={[
            "border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text)] shadow-xl",
            density === "compact" ? "max-w-xs rounded-md p-2 text-xs" : "max-w-sm rounded-lg p-3 text-sm",
            panelClassName,
          ].filter(Boolean).join(" ")}>
          {typeof children === "function" ? children({close}) : children}
        </OtsThemeRoot>
      </PopoverPanel>
    </>}
  </Popover>;
};
